import React, { useState, useRef, useEffect } from 'react';
import { 
  Bot, Sparkles, Send, X, Copy, Check, ChevronDown, 
  BookOpen, HelpCircle, Lightbulb, GraduationCap, ShieldCheck, 
  Maximize2, Minimize2, ArrowRight, CornerDownLeft, RotateCcw
} from 'lucide-react';

// Specialized University Knowledge Base for AKSU Business School
const KNOWLEDGE_RESPONSES = {
  "positivism": {
    topic: "Positivism vs. Interpretivism in Business Research (DBA 801 / BUS 901)",
    reply: `### Positivism vs. Interpretivism in Management Research

As you embark on **DBA 801: Introduction to Research Philosophies and Paradigms**, contrasting these two epistemological stances is foundational:

#### 1. Positivism (The Natural Science Stance)
* **Ontology:** Realist — an objective, singular reality exists independent of human observation.
* **Epistemology:** The researcher is detached and objective. Knowledge consists of observable facts, measurable phenomena, and law-like generalisations.
* **Methodology:** Deductive, quantitative hypothesis testing, large sample sizes, and statistical validation (e.g. regression analysis in **DBA 802**).
* **Boardroom Example:** Testing whether *"Board gender diversity directly increases Return on Assets (ROA) across 50 listed Nigerian manufacturing firms."*

#### 2. Interpretivism (The Human Meaning Stance)
* **Ontology:** Relativist / Social Constructionist — reality is socially constructed through meanings, language, and shared experiences.
* **Epistemology:** The researcher is empathetic and actively immersed. Focuses on the subjective nuances of executive decision-making.
* **Methodology:** Inductive, qualitative inquiry, in-depth executive interviews, phenomenology, and thematic analysis.
* **Boardroom Example:** Exploring *"How Chief Executive Officers in Port Harcourt navigate regulatory ambiguity during sudden petroleum subsidy removals."*

> **Silicon Tip for Scholars:** Most executive DBA dissertations benefit immensely from **Pragmatism** or **Critical Realism**, allowing you to combine quantitative metrics with qualitative managerial insights to solve industry dilemmas!`
  },
  "ontology": {
    topic: "Ontology vs. Epistemology Explained",
    reply: `### Decoupling Ontology and Epistemology

In **Lesson 2 of DBA 801**, these two concepts form the bedrock of your research proposal:

1. **Ontology (What is Reality?):**
   * Concerns the nature of reality and existence.
   * *The central question:* Is reality objective and external to social actors (Realism), or is it created by the perceptions and actions of individuals (Constructionism)?
   * *Example:* Is "corporate culture" an objective asset you can measure, or is it an evolving set of shared narratives created by employees?

2. **Epistemology (What Constitutes Valid Knowledge?):**
   * Concerns the theory of knowledge and how we come to know reality.
   * *The central question:* What claims can we accept as authoritative knowledge, and how can the researcher communicate this knowledge to others?
   * *Example:* Can valid knowledge about corporate risk be captured solely through balance sheet figures (Positivist), or must it include informal boardroom sentiments (Interpretivist)?

3. **Axiology (The Role of Values):**
   * Considers what role the researcher’s own ethics, corporate experience, and personal values play in the investigation.

Together, your ontological and epistemological assumptions dictate which research strategy you adopt in **Unit T1**!`
  },
  "pragmatism": {
    topic: "Pragmatism & Critical Realism in Executive Business Research",
    reply: `### Why Pragmatism is the Premier Stance for DBA Scholars

For executive doctoral researchers balancing corporate leadership and academic rigour:

#### 1. Core Tenet of Pragmatism
* Pragmatism sidesteps abstract philosophical warfare by asking: *"Does this research solve a concrete, high-stakes organizational problem?"*
* **Ontological position:** Accepts both singular objective realities (e.g. quarterly revenue losses, supply chain failure rates) and socially constructed realities (e.g. leadership trust, organizational morale).
* **Epistemological position:** Knowledge is valued primarily by its practical consequences and problem-solving utility in real-world governance.

#### 2. The Power of Mixed Methods
* Under a pragmatic paradigm, you are free to deploy **Mixed Methods (Saunders Onion Layer 3)**:
  * *Phase 1 (Quantitative):* Survey 150 operations managers across the Niger Delta to identify correlation patterns between digitisation and downtime.
  * *Phase 2 (Qualitative):* Conduct 8 in-depth executive interviews with Executive Directors to understand the organizational bottlenecks behind those numbers.

> **Silicon Defense Tip:** When your viva panel asks *"Why didn't you choose pure Positivism?"*, you can confidently defend that corporate problems are multi-faceted and demand pragmatic methodological triangulation!`
  },
  "onion": {
    topic: "The Saunders Research Onion Framework",
    reply: `### Saunders' Research Onion: Step-by-Step for AKSU Scholars

The **Research Onion** (Saunders, Lewis & Thornhill) provides a structured sequence for developing a defensible methodology:

* **Layer 1: Research Philosophies:** Positivism, Critical Realism, Interpretivism, Postmodernism, Pragmatism.
* **Layer 2: Research Approaches:** 
  * *Deductive:* Theory $\\rightarrow$ Hypothesis $\\rightarrow$ Data $\\rightarrow$ Confirmation (Positivist).
  * *Inductive:* Data $\\rightarrow$ Pattern Recognition $\\rightarrow$ Theory Building (Interpretivist).
  * *Abductive:* Surprising empirical anomaly $\\rightarrow$ Iterative theory/data oscillation (Pragmatic).
* **Layer 3: Methodological Choice:** Mono-method quantitative, mono-method qualitative, mixed methods, or multi-method.
* **Layer 4: Research Strategies:** Case study, survey, archival research, action research, grounded theory, ethnography.
* **Layer 5: Time Horizons:** Cross-sectional (snapshot in time) vs. Longitudinal (change over months/years).
* **Layer 6: Techniques & Procedures:** Data collection instruments, questionnaire design, sampling procedures, and statistical estimation.

> **Continuous Assessment 1 Guidance:** Your **Continuous Assessment 1** requires mapping your proposed dissertation dilemma across all 6 layers!`
  },
  "problem": {
    topic: "Formulating a Practitioner-Led Research Problem Statement",
    reply: `### Structuring an Executive Problem Statement for DBA / PhD

A doctoral-level problem statement must satisfy two demanding masters: **Boardroom Relevance** and **Academic Rigour**. Follow this 4-part framework:

1. **The Context & Ideal State:**
   * Describe what should ideally happen in optimal corporate practice (e.g., *"Downstream petroleum distribution hubs require uninterrupted supply chain visibility to maintain regional price stability..."*).
2. **The Empirical Dilemma (The Reality Gap):**
   * Highlight the breakdown or persistent failure in practice supported by recent industry data (e.g., *"However, infrastructural bottlenecks and pipeline downtime along the Calabar-Itu corridor result in 38% transit delays..."*).
3. **The Theoretical / Knowledge Void:**
   * Why have existing academic models failed to solve this? (e.g., *"While existing supply chain models focus on Western multi-modal logistics, few empirical studies investigate resilience strategies under informal regional constraints in Nigeria..."*).
4. **The Direct Consequences & Research Purpose:**
   * Explicitly declare the tangible costs of inaction and how your investigation will produce both novel theoretical models and actionable managerial blueprints.`
  },
  "apa": {
    topic: "APA 7th Edition Referencing Guidelines",
    reply: `### APA 7th Referencing Standards for AKSU Postgraduates

AKSU Business School mandates strict adherence to the **American Psychological Association (APA) 7th Edition**. Here are the most common formats:

#### 1. Peer-Reviewed Academic Journal Article
> Author, A. A., & Author, B. B. (2025). Title of article in sentence case. *Title of Periodical in Italics*, 14(2), 112–128. https://doi.org/xxxx

#### 2. Institutional / Government Regulatory Report
> Central Bank of Nigeria. (2025). *Macroeconomic outlook and monetary policy report for the third quarter* (CBN Publication No. 89). Central Bank of Nigeria.

#### 3. Corporate Annual Report
> Seplat Energy Plc. (2025). *Annual report and accounts: Powering domestic prosperity*. https://www.seplatenergy.com/investors

#### 4. Doctoral Dissertation or Master's Thesis
> Udoh, E. (2026). *Evaluating strategic agility and operational resilience in downstream petroleum logistics* [Doctoral dissertation, Akwa Ibom State University]. AKSU Institutional Repository.

#### In-Text Citations:
* **Parenthetical:** ...strategic agility mitigates sudden logistics disruption (Johnson & Uwah, 2026).
* **Narrative:** According to Johnson and Uwah (2026), strategic agility is paramount...`
  },
  "framework": {
    topic: "Theoretical vs Conceptual Frameworks",
    reply: `### Theoretical Framework vs. Conceptual Framework

Your proposal defense will rigorously scrutinize the distinction between these two structures:

#### 1. Theoretical Framework (The Established Foundation)
* Built upon existing, established, and tested theories from scholarly literature (e.g., *Resource-Based View (RBV)* by Barney, *Dynamic Capabilities Theory* by Teece, or *Agency Theory* by Jensen & Meckling).
* It provides the theoretical lens and explanatory mechanism for why variables relate to one another.

#### 2. Conceptual Framework (Your Specific Blueprint)
* The researcher's own synthesized diagram or model of how variables interact in your specific study:
  * **Independent Variable(s) [IV]:** Predictive or causal drivers (e.g. Digital Transformation, Predictive Maintenance).
  * **Mediating Variable(s) [MV]:** Intervening mechanisms (e.g. Operational Agility).
  * **Moderating Variable(s) [MoV]:** Boundary conditions altering the strength of the relationship (e.g. Regulatory Compliance).
  * **Dependent Variable(s) [DV]:** Outcome metric (e.g. Firm Performance, Downtime Reduction).

> **Rule of Thumb:** Theory gives you the *rationale*; the conceptual model gives you the *measurable roadmap*!`
  },
  "sample": {
    topic: "Sample Size Determination & Taro Yamane Formula",
    reply: `### Sample Size Calculation for Business Research

When determining defensible sample size for quantitative surveys in **DBA 801 / DBA 802**:

#### 1. Taro Yamane Formula (For Known / Finite Populations)
$$n = \\frac{N}{1 + N(e)^2}$$
Where:
* $n$ = Desired sample size
* $N$ = Total finite target population (e.g. 500 registered oilfield service contractors)
* $e$ = Margin of error allowable (typically 0.05 for 95% confidence level)

**Example Calculation:**
If $N = 500$ and $e = 0.05$:
$$n = \\frac{500}{1 + 500(0.0025)} = \\frac{500}{1 + 1.25} = \\frac{500}{2.25} \\approx 222 \\text{ respondents}$$

#### 2. Cochran's Formula (For Infinite / Unknown Populations)
$$n_0 = \\frac{Z^2 p q}{e^2}$$

#### 3. Structural Equation Modeling (PLS-SEM Rule)
* For SmartPLS 4, use the **10-times rule**: Sample size must be at least 10 times the maximum number of structural paths directed at any latent construct in the inner model.`
  },
  "turnitin": {
    topic: "Plagiarism, Turnitin & Academic Integrity",
    reply: `### AKSU Postgraduate Code of Academic Integrity

AKSU Business School enforces strict automated anti-plagiarism screening via Turnitin:

* **Maximum Similarity Index:** The University Senate mandates an overall similarity index of **less than 15%** (excluding bibliography, common nomenclature, and official statutory titles).
* **Single Source Threshold:** No single publication or internet source may contribute more than **1%** to the total document.
* **Artificial Intelligence Attribution:** All AI-assisted research querying (including prompts through Silicon) must be declared in your *Methodological Notes & Reflexivity Appendix*.
* **Direct Quotations:** Never exceed two consecutive sentences of direct quotation without quotation marks and specific page attribution (e.g., Johnson, 2026, p. 44).`
  },
  "defense": {
    topic: "Preparing for Proposal Defense & Viva Voce",
    reply: `### Viva Voce & Defense Preparation: What AKSU Panels Ask

The AKSU Business School Postgraduate Board evaluates four core pillars during your oral defense:

1. **The 'So What?' Test:** Why does this problem matter to the Nigerian economy, enterprise governance, or public administration?
2. **Epistemological Alignment:** Does your data collection strategy faithfully match your stated paradigm (e.g., don't claim to be an Interpretivist while using a rigid 5-point Likert scale questionnaire with no open-ended prompts)?
3. **Construct Validity & Reliability:** How did you validate that your survey instrument genuinely measures what it claims to measure (Cronbach’s alpha $\\ge 0.70$, Composite Reliability, AVE $\\ge 0.50$)?
4. **Original Contribution to Practice:** As a doctoral scholar, what new framework or actionable toolkit will C-Suite executives or policymakers adopt based on your findings?`
  },
  "dba802": {
    topic: "Preparation for DBA 802: Analytic Techniques",
    reply: `### Preview: DBA 802 Analytic Techniques (Term 2 • 6 Credits)

As you prepare for Unit **T2 (DBA 802)** with Dr. O. T. Matthew:

* **Key Focus:** Quantitative estimation methods including multiple regression, exploratory/confirmatory factor analysis, and Partial Least Squares Structural Equation Modelling (PLS-SEM).
* **Recommended Software Tooling:**
  1. **SmartPLS 4:** Ideal for complex composite structural models with moderation and mediation.
  2. **Stata / SPSS 29:** Used for descriptive metrics, ANOVA, and panel data econometric models.
  3. **NVivo 14:** Used for coding qualitative executive interviews.
* **Term 1 Tip:** While mastering **DBA 801**, clearly identify whether your variables will require primary survey instruments or secondary econometric panel data!`
  }
};

const PROMPT_SUGGESTIONS = [
  { label: "Positivism vs. Interpretivism", key: "positivism" },
  { label: "Ontology vs. Epistemology", key: "ontology" },
  { label: "Why Pragmatism for DBA?", key: "pragmatism" },
  { label: "Saunders' Research Onion", key: "onion" },
  { label: "Executive Problem Statement", key: "problem" },
  { label: "Theoretical vs Conceptual Model", key: "framework" },
  { label: "Sample Size (Taro Yamane)", key: "sample" },
  { label: "APA 7th Referencing", key: "apa" },
  { label: "Viva Defense Preparation", key: "defense" },
  { label: "Turnitin Integrity Standards", key: "turnitin" },
  { label: "DBA 802 Analytics Preview", key: "dba802" }
];

export const SiliconAssistant = ({ 
  isOpen = true, 
  onClose, 
  onToggle, 
  currentStudent, 
  student,
  activeModule, 
  currentModule,
  activeCourse,
  activeCourseCode,
  activeLessonTitle,
  studentDegree,
  initialPrompt = null,
  isInline = false
}) => {
  // Normalize incoming student and module data
  const studentData = currentStudent || student || {
    fullName: "Scholar",
    degreeLevel: studentDegree || "Doctoral",
    programmeName: "Executive Programme",
    researchTopic: "Strategic Management and Operational Resilience in the Nigerian Corridor",
    supervisor: "Prof. Asuquo E. Johnson, PhD"
  };

  const resolvedCourseCode = activeCourse?.courseCode || activeCourse?.code || activeCourseCode || "DBA 911";
  const resolvedLessonTitle = activeModule?.title || currentModule?.title || activeLessonTitle || activeCourse?.title || "Foundations of Scientific Inquiry and the Nature of Knowledge";

  const moduleData = {
    code: resolvedCourseCode,
    title: resolvedLessonTitle
  };

  const handleClose = onClose || onToggle || (() => {});

  const [messages, setMessages] = useState([
    {
      id: "welcome-1",
      sender: "silicon",
      text: `Greetings, ${studentData.fullName}! I am **Silicon**, your dedicated AI Academic Learning Assistant at Akwa Ibom State University Business School.

I am configured with full knowledge of your **${studentData.programmeName}** curriculum, research philosophies (**${moduleData.code}: ${moduleData.title}**), APA 7th academic standards, and dissertation milestone requirements.

How may I assist your scholarly inquiry today? Click any suggested topic below or type your research question.`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);

  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Handle initial prompt if passed
  useEffect(() => {
    if (initialPrompt && typeof initialPrompt === 'string' && initialPrompt.trim()) {
      handleSend(initialPrompt);
    }
  }, [initialPrompt]);

  const handleSend = (textToSend = null) => {
    const text = (textToSend || inputValue).trim();
    if (!text) return;

    const userMessage = {
      id: `usr-${Date.now()}`,
      sender: "user",
      text: text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    if (!textToSend) setInputValue("");
    setIsTyping(true);

    // Generate responsive academic answer
    setTimeout(() => {
      const lower = text.toLowerCase();
      let matchedKey = null;

      if (lower.includes("pragmat")) {
        matchedKey = "pragmatism";
      } else if (lower.includes("positiv") || lower.includes("interpret") || lower.includes("paradigm") || lower.includes("epistemolog")) {
        matchedKey = "positivism";
      } else if (lower.includes("onto") || lower.includes("reality") || lower.includes("axiol")) {
        matchedKey = "ontology";
      } else if (lower.includes("onion") || lower.includes("saunders") || lower.includes("layer")) {
        matchedKey = "onion";
      } else if (lower.includes("problem") || lower.includes("statement") || lower.includes("dilemma") || lower.includes("gap")) {
        matchedKey = "problem";
      } else if (lower.includes("theoret") || lower.includes("concept") || lower.includes("framework") || lower.includes("variable")) {
        matchedKey = "framework";
      } else if (lower.includes("sample") || lower.includes("yamane") || lower.includes("population") || lower.includes("cochran")) {
        matchedKey = "sample";
      } else if (lower.includes("apa") || lower.includes("citation") || lower.includes("reference") || lower.includes("cite")) {
        matchedKey = "apa";
      } else if (lower.includes("plagiarism") || lower.includes("turnitin") || lower.includes("similarity") || lower.includes("integrity")) {
        matchedKey = "turnitin";
      } else if (lower.includes("defense") || lower.includes("viva") || lower.includes("panel") || lower.includes("oral")) {
        matchedKey = "defense";
      } else if (lower.includes("dba 802") || lower.includes("analytic") || lower.includes("pls") || lower.includes("smartpls") || lower.includes("spss") || lower.includes("stata")) {
        matchedKey = "dba802";
      }

      let responseText = "";
      if (matchedKey && KNOWLEDGE_RESPONSES[matchedKey]) {
        responseText = KNOWLEDGE_RESPONSES[matchedKey].reply;
      } else if (lower.includes("hello") || lower.includes("hi") || lower.includes("hey") || lower.includes("good morning") || lower.includes("good afternoon")) {
        responseText = `### Greetings, ${studentData.fullName}!

I am ready to assist with your academic progress in **${moduleData.code}: ${moduleData.title}**.

Here are some areas where I can help right now:
* Formulating your **Research Problem Statement**
* Navigating **Saunders' Research Onion** for methodology
* Contrasting **Positivism, Interpretivism, and Pragmatism**
* Formatting in-text citations & references according to **APA 7th Edition**
* Calculating sample sizes using the **Taro Yamane formula**

Please select any topic from the suggested prompts below or describe your specific research challenge!`;
      } else {
        responseText = `### Scholarly Guidance from Silicon AI

Thank you for your academic inquiry regarding: **"${text}"**.

As a scholar in the **${studentData.programmeName}** advancing through **${moduleData.code}: ${moduleData.title}**:

1. **Conceptual Alignment:** Situate this inquiry within your approved research domain: *"${studentData.researchTopic}"*.
2. **Methodological Defensibility:** Ensure every analytical choice aligns with Saunders' Research Onion (from ontology and epistemology through to data collection procedures).
3. **Faculty & Senate Standard:** Adhere to AKSU Postgraduate Regulations and consult your faculty advisor (**${studentData.supervisor}**).

Feel free to click any of the suggested topics below to explore specific research philosophies, sample size formulas, or APA 7th standards!`;
      }

      const botMessage = {
        id: `sil-${Date.now()}`,
        sender: "silicon",
        text: responseText,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 450);
  };

  const handleCopy = (id, text) => {
    navigator.clipboard?.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Render markdown-like elements simply
  const renderMessageContent = (text) => {
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      if (line.startsWith('### ')) {
        return <h4 key={idx} className="text-sm font-black font-sc text-[#7A470E] mt-2 mb-1">{line.replace('### ', '')}</h4>;
      }
      if (line.startsWith('#### ')) {
        return <h5 key={idx} className="text-xs font-black font-sc text-[#0B2644] mt-2 mb-0.5">{line.replace('#### ', '')}</h5>;
      }
      if (line.startsWith('* ') || line.startsWith('- ')) {
        return (
          <li key={idx} className="ml-4 list-disc text-xs text-slate-700 leading-relaxed my-0.5">
            <span dangerouslySetInnerHTML={{ __html: formatInline(line.substring(2)) }} />
          </li>
        );
      }
      if (line.match(/^\d+\.\s/)) {
        const content = line.replace(/^\d+\.\s/, '');
        return (
          <div key={idx} className="ml-2 flex items-start space-x-1.5 my-1 text-xs text-slate-700 leading-relaxed">
            <span className="font-bold text-[#7A470E] font-sc shrink-0">{line.match(/^\d+\./)[0]}</span>
            <span dangerouslySetInnerHTML={{ __html: formatInline(content) }} />
          </div>
        );
      }
      if (line.startsWith('> ')) {
        return (
          <div key={idx} className="p-2.5 my-2 rounded-lg bg-amber-50/70 border-l-4 border-[#7A470E] text-xs text-slate-800 italic">
            <span dangerouslySetInnerHTML={{ __html: formatInline(line.replace('> ', '')) }} />
          </div>
        );
      }
      if (line.trim() === '') {
        return <div key={idx} className="h-1.5" />;
      }
      return (
        <p key={idx} className="text-xs text-slate-700 leading-relaxed my-0.5">
          <span dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
        </p>
      );
    });
  };

  const formatInline = (str) => {
    return str
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-slate-900">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-slate-100 font-mono text-[11px] text-[#7A470E]">$1</code>');
  };

  // Content JSX shared between Inline and Drawer views
  const chatInterface = (
    <div className="flex flex-col h-full overflow-hidden bg-white rounded-3xl">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white p-4 flex items-center justify-between border-b-2 border-[#7A470E] relative overflow-hidden shrink-0 rounded-t-3xl">
        <div className="absolute right-0 top-0 w-48 h-48 bg-[#7A470E]/15 rounded-full blur-2xl pointer-events-none" />

        <div className="flex items-center space-x-3 relative z-10">
          <div className="relative">
            <div className="w-10 h-10 rounded-2xl bg-white text-[#7A470E] flex items-center justify-center shadow-lg font-black">
              <Bot className="w-6 h-6 text-[#7A470E]" />
            </div>
            <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white animate-pulse" />
          </div>

          <div>
            <div className="flex items-center space-x-2">
              <h3 className="font-black text-base font-sc tracking-wide text-white">
                Silicon
              </h3>
              <span className="bg-[#7A470E] text-white text-[10px] font-mono px-2 py-0.5 rounded-full font-bold">
                Academic AI
              </span>
            </div>
            <div className="text-[11px] text-[#B87A28] font-sc">
              Curriculum & Methodology Assistant · {moduleData.code}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-1 relative z-10 text-white/80">
          {!isInline && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
              title={isExpanded ? "Collapse view" : "Expand view"}
            >
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={handleClose}
            className="p-1.5 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
            title="Close Silicon"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Active Course Context Strip */}
      <div className="bg-amber-50/80 px-4 py-2 border-b border-[#7A470E]/30 flex items-center justify-between text-[11px] text-slate-700 shrink-0">
        <div className="flex items-center space-x-1.5 truncate">
          <GraduationCap className="w-3.5 h-3.5 text-[#7A470E] shrink-0" />
          <span className="font-bold text-[#7A470E] font-sc">Active Course:</span>
          <span className="truncate font-semibold">{moduleData.code}: {moduleData.title}</span>
        </div>
        <span className="bg-emerald-100 text-[#7A470E] px-2 py-0.5 rounded text-[10px] font-bold font-mono shrink-0">
          Online & Ready
        </span>
      </div>

      {/* Chat Messages Feed */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/60 min-h-[250px]">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}
          >
            <div className="flex items-center space-x-1 text-[10px] text-slate-400 mb-1 px-1">
              <span className="font-bold font-sc">
                {msg.sender === 'user' ? studentData.fullName : "Silicon AI"}
              </span>
              <span>•</span>
              <span>{msg.timestamp}</span>
            </div>

            <div
              className={`p-4 rounded-2xl max-w-[90%] sm:max-w-[85%] text-xs leading-relaxed relative shadow-xs ${
                msg.sender === 'user'
                  ? 'bg-[#0B2644] text-white rounded-br-none'
                  : 'bg-white text-slate-800 border-2 border-slate-200 rounded-bl-none'
              }`}
            >
              {msg.sender === 'user' ? (
                <p className="text-white font-medium whitespace-pre-wrap">{msg.text}</p>
              ) : (
                <div>
                  {renderMessageContent(msg.text)}
                  <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                    <span className="italic font-sc text-[10px] text-[#7A470E]">
                      ✓ Aligned with AKSU Postgraduate Academic Code
                    </span>
                    <button
                      onClick={() => handleCopy(msg.id, msg.text)}
                      className="hover:text-[#7A470E] transition-colors flex items-center space-x-1 cursor-pointer"
                    >
                      {copiedId === msg.id ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-[#7A470E]" />
                          <span className="text-[#7A470E] font-bold">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex items-center space-x-2 p-3 bg-white rounded-2xl border border-slate-200 w-36 text-slate-500 text-xs shadow-xs">
            <span className="w-2 h-2 rounded-full bg-[#7A470E] animate-ping" />
            <span className="font-sc text-[11px] text-[#7A470E] font-bold">Consulting literature...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Academic Inquiry Chips */}
      <div className="p-2.5 bg-white border-t border-slate-200 shrink-0">
        <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 font-sc mb-1 px-1 flex items-center space-x-1">
          <Lightbulb className="w-3 h-3 text-[#7A470E]" />
          <span>Quick Topic Inquiries:</span>
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
          {PROMPT_SUGGESTIONS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => handleSend(KNOWLEDGE_RESPONSES[item.key]?.topic || item.label)}
              className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-amber-50 hover:text-[#7A470E] hover:border-[#7A470E] border border-slate-200 text-[11px] font-bold shrink-0 transition-colors cursor-pointer font-sc text-slate-700"
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Form Input Box */}
      <div className="p-3 bg-white border-t border-slate-200 shrink-0 rounded-b-3xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex items-center space-x-2"
        >
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={`Ask Silicon about ${moduleData.code}, research methodology, APA referencing...`}
            className="flex-1 bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#7A470E] transition-colors"
          />
          <button
            type="submit"
            disabled={!inputValue.trim()}
            className="px-4 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] disabled:opacity-40 text-white rounded-xl font-bold text-xs shadow-xs transition-colors flex items-center space-x-1 cursor-pointer font-sc shrink-0"
          >
            <span>Send</span>
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      </div>
    </div>
  );

  // If used directly embedded in a tab or docked sidebar
  if (isInline) {
    return (
      <div className="rounded-3xl border-2 border-[#7A470E] overflow-hidden shadow-md h-full flex flex-col">
        {chatInterface}
      </div>
    );
  }

  // Floating Drawer / Modal Mode
  return (
    <>
      {/* Floating Action Trigger when closed */}
      {!isOpen && (
        <button
          onClick={handleClose}
          className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-[#030F1C] via-[#0B2644] to-[#7A470E] text-white p-3.5 sm:px-5 sm:py-3.5 rounded-full shadow-2xl hover:shadow-red-900/40 flex items-center space-x-3 transition-all transform hover:scale-105 cursor-pointer border-2 border-white/40 group animate-bounce-subtle"
        >
          <div className="relative">
            <div className="w-9 h-9 rounded-full bg-white text-[#7A470E] flex items-center justify-center shadow-inner font-black text-sm">
              <Sparkles className="w-5 h-5 text-[#7A470E]" />
            </div>
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
          </div>
          <div className="hidden sm:block text-left">
            <div className="text-xs font-black font-sc tracking-wide leading-tight flex items-center space-x-1.5">
              <span>Ask Silicon AI</span>
              <span className="bg-white/20 text-[10px] px-1.5 py-0.2 rounded font-mono font-normal">Active</span>
            </div>
            <div className="text-[10px] text-white/80 font-sc">AKSU Research Learning Assistant</div>
          </div>
        </button>
      )}

      {/* Main Silicon AI Modal / Drawer */}
      {isOpen && (
        <div 
          className={`fixed z-50 transition-all duration-300 shadow-2xl border-4 border-[#7A470E] bg-white flex flex-col overflow-hidden ${
            isExpanded 
              ? 'inset-4 sm:inset-8 rounded-3xl' 
              : 'bottom-4 right-4 sm:bottom-6 sm:right-6 w-[94vw] sm:w-[480px] h-[640px] max-h-[90vh] rounded-3xl'
          }`}
        >
          {chatInterface}
        </div>
      )}
    </>
  );
};
